import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowRightLeft,
  BadgeDollarSign,
  BarChart3,
  Building2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  FileMinus,
  FileText,
  Package,
  RefreshCw,
  ShoppingCart,
  TrendingUp,
  Users,
  Wallet,
  CheckCircle2,
  AlertCircle,
  Layers,
  LayoutDashboard,
  CalendarDays,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showError } from '../components/Alerts';
import dashboardService, {
  DashboardDTO,
  RecentTransaction,
} from '../services/dashboardService';
import { formatMoney } from '../utils/format';
import { cn } from '../lib/utils';
import { PERMISSIONS } from '../constants/permissions';

// ── Helpers ───────────────────────────────────────────────────────────────────

const TX_TYPE_LABELS: Record<string, string> = {
  COBRANZA: 'Cobranza',
  EGRESO: 'Egreso',
  INGRESO: 'Ingreso',
  TRANSFERENCIA_INTERNA: 'Transferencia',
  CAMBIO_MONEDA: 'Cambio de moneda',
  AJUSTE_MANUAL: 'Ajuste manual',
  DEVOLUCION: 'Devolución',
  PAGO_PROVEEDOR: 'Pago proveedor',
};

const TX_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  COMPLETADA: { label: 'Completada', className: 'bg-success/10 text-success' },
  PENDIENTE:  { label: 'Pendiente',  className: 'bg-warning/10 text-warning' },
  CANCELADA:  { label: 'Cancelada',  className: 'bg-destructive/10 text-destructive' },
  REVERTIDA:  { label: 'Revertida',  className: 'bg-muted text-muted-foreground' },
};

function getTxIcon(type: string) {
  switch (type) {
    case 'COBRANZA':              return <ArrowDownLeft  className="w-4 h-4 text-success" />;
    case 'EGRESO':                return <ArrowUpRight   className="w-4 h-4 text-destructive" />;
    case 'PAGO_PROVEEDOR':        return <FileMinus      className="w-4 h-4 text-destructive" />;
    case 'TRANSFERENCIA_INTERNA': return <ArrowRightLeft className="w-4 h-4 text-info" />;
    case 'CAMBIO_MONEDA':         return <BadgeDollarSign className="w-4 h-4 text-warning" />;
    case 'DEVOLUCION':            return <ArrowUpRight   className="w-4 h-4 text-warning" />;
    default:                      return <ArrowDownLeft  className="w-4 h-4 text-muted-foreground" />;
  }
}

function greetingText() {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 18) return 'Buenas tardes';
  return 'Buenas noches';
}

// ── Mini donut (for sold/available UF) ────────────────────────────────────────

function MiniDonut({ pct, size = 56 }: { pct: number; size?: number }) {
  const r = size * 0.38;
  const circumference = 2 * Math.PI * r;
  const dash = (pct / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--muted)" strokeWidth={size * 0.14} />
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke="hsl(var(--success))" strokeWidth={size * 0.14}
        strokeDasharray={`${dash} ${circumference}`}
        strokeDashoffset={0}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        strokeLinecap="round"
        className="transition-all duration-700"
      />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.24} fontWeight="700" fill="var(--foreground)">
        {pct}%
      </text>
    </svg>
  );
}

// ── Stacked bar (for PO / Invoice statuses) ──────────────────────────────────

function StackedBar({ segments }: { segments: { value: number; color: string; label: string }[] }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  return (
    <div className="w-full flex rounded-full overflow-hidden h-2.5">
      {segments.filter(s => s.value > 0).map((s) => (
        <div
          key={s.label}
          className={cn('h-full transition-all duration-500', s.color)}
          style={{ width: `${(s.value / total) * 100}%` }}
          title={`${s.label}: ${s.value}`}
        />
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── MAIN DASHBOARD ────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export default function Dashboard() {
  const { hasPermission, user }       = useAuth();
  const canAccessDashboard            = hasPermission(PERMISSIONS.DASHBOARD.VER);

  const [data, setData]               = useState<DashboardDTO | null>(null);
  const [loading, setLoading]         = useState(canAccessDashboard);
  const [showCashBoxes, setShowCashBoxes] = useState(false);

  const load = useCallback(async () => {
    if (!canAccessDashboard) return;
    setLoading(true);
    try {
      const result = await dashboardService.getDashboard();
      setData(result);
    } catch {
      showError('No se pudo cargar el dashboard. Verificá tu conexión.');
    } finally {
      setLoading(false);
    }
  }, [canAccessDashboard]);

  useEffect(() => { load(); }, [load]);

  // Sin permisos → pantalla de bienvenida sin llamar al API
  if (!canAccessDashboard) {
    return (
      <div className="w-full h-full p-4 sm:p-6 flex items-center justify-center">
        <div className="w-full max-w-lg text-center">
          {/* Icono */}
          <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mx-auto mb-6">
            <LayoutDashboard className="w-10 h-10 text-primary" />
          </div>

          {/* Saludo */}
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {greetingText()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-muted-foreground mb-8">
            Bienvenido al sistema ERP. Todavía no tenés acceso al panel de control.
          </p>

          {/* Card explicativa */}
          <div className="rounded-xl border border-border bg-card p-6 text-left space-y-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-warning/10 flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Sin permisos asignados</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Tu cuenta está activa pero tu rol todavía no tiene permisos configurados para visualizar el panel ni otras secciones del sistema.
                </p>
              </div>
            </div>

            <div className="border-t border-border" />

            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-info/10 flex-shrink-0 mt-0.5">
                <Users className="w-4 h-4 text-info" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">¿Cómo obtener acceso?</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Comunicate con el administrador de tu organización para que te asigne un rol con los permisos correspondientes a tus responsabilidades.
                </p>
              </div>
            </div>

            <div className="border-t border-border" />

            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-success/10 flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Lo que podés hacer ahora</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Podés consultar el listado de usuarios del sistema en la sección <strong className="text-foreground">Usuarios</strong>, dentro del menú de Administración.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            Sesión iniciada como <span className="font-medium text-foreground">{user?.email}</span>
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingScreen message="Cargando dashboard" />;

  if (!data) {
    return (
      <div className="w-full h-full p-4 sm:p-6">
        <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
          <div className="py-16 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h3 className="mb-2 text-lg font-medium text-foreground">No se pudo obtener la información</h3>
            <p className="mb-4 text-sm text-muted-foreground">Verificá tu conexión e intentá nuevamente</p>
            <Button onClick={load} className="rounded-lg bg-primary text-primary-foreground hover:opacity-90">
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const {
    financialSummary, salesSummary, installmentsSummary,
    purchaseOrdersSummary, supplierInvoicesSummary,
    projectsSummary, entityCounts, recentTransactions,
  } = data;

  const soldPct = projectsSummary.totalFunctionalUnits > 0
    ? Math.round((projectsSummary.soldFunctionalUnits / projectsSummary.totalFunctionalUnits) * 100) : 0;
  const canSeeFinanzas = hasPermission(PERMISSIONS.DASHBOARD.VER, PERMISSIONS.DASHBOARD.FINANZAS);
  const canSeeCompras  = hasPermission(PERMISSIONS.DASHBOARD.VER, PERMISSIONS.DASHBOARD.COMPRAS);
  const canSeeVentas   = hasPermission(PERMISSIONS.DASHBOARD.VER, PERMISSIONS.DASHBOARD.VENTAS);

  const today = new Date();
  const dateStr = today.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">

      {/* ── Page header (no card, just text) ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {greetingText()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
            <CalendarDays className="w-3.5 h-3.5" />
            {dateStr.charAt(0).toUpperCase() + dateStr.slice(1)}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="flex items-center gap-2 rounded-lg">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* ── KPI Cards (top row) ── */}
      {canSeeFinanzas && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* ARS */}
          <div className="rounded-xl border border-border bg-card p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary rounded-l-xl" />
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Balance ARS</p>
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Wallet className="w-3.5 h-3.5 text-primary" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums break-all">
              $ {formatMoney(financialSummary.totalBalanceARS)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {financialSummary.cashBoxes.filter(c => c.currency === 'ARS').length} caja{financialSummary.cashBoxes.filter(c => c.currency === 'ARS').length !== 1 ? 's' : ''} activas
            </p>
          </div>

          {/* USD */}
          <div className="rounded-xl border border-border bg-card p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-success rounded-l-xl" />
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Balance USD</p>
              <div className="p-1.5 rounded-lg bg-success/10">
                <BadgeDollarSign className="w-3.5 h-3.5 text-success" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums break-all">
              U$S {formatMoney(financialSummary.totalBalanceUSD)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {financialSummary.cashBoxes.filter(c => c.currency === 'USD').length} caja{financialSummary.cashBoxes.filter(c => c.currency === 'USD').length !== 1 ? 's' : ''} activas
            </p>
          </div>

          {/* Ventas */}
          <div className="rounded-xl border border-border bg-card p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-info rounded-l-xl" />
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ventas totales</p>
              <div className="p-1.5 rounded-lg bg-info/10">
                <TrendingUp className="w-3.5 h-3.5 text-info" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums">{salesSummary.totalSales}</p>
            <p className="text-xs mt-1">
              <span className="inline-flex items-center font-medium text-info bg-info/10 px-1.5 py-0.5 rounded-md">
                +{salesSummary.salesThisMonth} este mes
              </span>
            </p>
          </div>

          {/* Clientes */}
          <div className="rounded-xl border border-border bg-card p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-warning rounded-l-xl" />
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Clientes activos</p>
              <div className="p-1.5 rounded-lg bg-warning/10">
                <Users className="w-3.5 h-3.5 text-warning" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums">{entityCounts.activeCustomers}</p>
            <p className="text-xs text-muted-foreground mt-1">{entityCounts.totalCustomers} registrados en total</p>
          </div>
        </div>
      )}

      {/* ── Cash box detail (standalone collapsible) ── */}
      {canSeeFinanzas && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowCashBoxes(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Wallet className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-sm font-medium text-foreground">Detalle de cajas</span>
              <span className="text-xs text-muted-foreground">({financialSummary.cashBoxes.length} activas)</span>
            </div>
            {showCashBoxes
              ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {showCashBoxes && (
            <div className="border-t border-border">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Caja</th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Moneda</th>
                    <th className="px-5 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {financialSummary.cashBoxes.map((cb, idx) => (
                    <tr key={cb.id} className={cn('hover:bg-muted/30 transition-colors', idx < financialSummary.cashBoxes.length - 1 && 'border-b border-border')}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
                            <Wallet className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm text-foreground">{cb.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-medium uppercase text-muted-foreground">{cb.currency}</span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-sm font-semibold text-amount tabular-nums">
                          {cb.currency === 'USD' ? 'U$S' : '$'} {formatMoney(cb.balance)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Middle row: 2-col (Proyectos + Cuotas) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Proyectos & UF — visual card with donut */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
              <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Proyectos y Unidades Funcionales</h2>
              <p className="text-xs text-muted-foreground">{projectsSummary.totalProjects} proyectos activos</p>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-6">
              <MiniDonut pct={soldPct} size={80} />
              <div className="flex-1 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xl font-bold text-foreground">{projectsSummary.totalProjects}</p>
                    <p className="text-[11px] text-muted-foreground">Proyectos</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-success">{projectsSummary.soldFunctionalUnits}</p>
                    <p className="text-[11px] text-muted-foreground">Vendidas</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-info">{projectsSummary.availableFunctionalUnits}</p>
                    <p className="text-[11px] text-muted-foreground">Disponibles</p>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{projectsSummary.soldFunctionalUnits} de {projectsSummary.totalFunctionalUnits} UF vendidas</span>
                    <span className="font-semibold text-foreground">{soldPct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div className="bg-success h-2 rounded-full transition-all duration-500" style={{ width: `${soldPct}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cuotas */}
        {canSeeFinanzas && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ClipboardList className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Cuotas</h2>
                  <p className="text-xs text-muted-foreground">{installmentsSummary.totalPendingCount} pendientes</p>
                </div>
              </div>
              {installmentsSummary.overdueCount > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive bg-destructive/10 px-2.5 py-1 rounded-full">
                  <AlertTriangle className="w-3 h-3" />
                  {installmentsSummary.overdueCount} vencida{installmentsSummary.overdueCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="p-5 space-y-1">
              {installmentsSummary.overdueCount > 0 && (
                <div className="flex items-center gap-2 bg-destructive/5 text-destructive rounded-lg px-3 py-2.5 mb-3 border border-destructive/20">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span className="text-xs">
                    <span className="font-semibold">{installmentsSummary.overdueCount} vencida{installmentsSummary.overdueCount !== 1 ? 's' : ''}</span>
                    <span className="opacity-80"> — $ {formatMoney(installmentsSummary.overdueTotal)}</span>
                  </span>
                </div>
              )}
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" /> Próx. 30 días
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums">{installmentsSummary.upcomingNext30DaysCount}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">$ {formatMoney(installmentsSummary.upcomingNext30DaysTotal)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-3.5 h-3.5 text-success" /> Cobrado este mes
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums text-success">{installmentsSummary.paidThisMonthCount}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">$ {formatMoney(installmentsSummary.paidThisMonthTotal)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Pendientes totales
                  </span>
                  <p className="text-sm font-semibold tabular-nums">{installmentsSummary.totalPendingCount}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Bottom row: 3-col (OCs · Facturas · Ventas) ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* OCs */}
        {canSeeCompras && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <ShoppingCart className="w-4 h-4 text-warning" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Órdenes de compra</h2>
                <p className="text-xs text-muted-foreground">{purchaseOrdersSummary.totalOrders} órdenes</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <StackedBar segments={[
                { value: purchaseOrdersSummary.draftCount,     color: 'bg-muted-foreground', label: 'Borrador' },
                { value: purchaseOrdersSummary.sentCount,      color: 'bg-info',             label: 'Enviado' },
                { value: purchaseOrdersSummary.receivedCount,  color: 'bg-success',          label: 'Recibido' },
                { value: purchaseOrdersSummary.cancelledCount, color: 'bg-destructive',      label: 'Cancelado' },
              ]} />
              <div className="space-y-1.5">
                {[
                  { label: 'Borrador',  count: purchaseOrdersSummary.draftCount,     dot: 'bg-muted-foreground' },
                  { label: 'Enviado',   count: purchaseOrdersSummary.sentCount,      dot: 'bg-info' },
                  { label: 'Recibido',  count: purchaseOrdersSummary.receivedCount,  dot: 'bg-success' },
                  { label: 'Cancelado', count: purchaseOrdersSummary.cancelledCount, dot: 'bg-destructive' },
                ].map(({ label, count, dot }) => (
                  <div key={label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />
                      <span className="text-xs text-muted-foreground">{label}</span>
                    </div>
                    <span className="text-xs font-semibold tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-border space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Compr. ARS</span>
                  <span className="font-semibold text-amount tabular-nums">$ {formatMoney(purchaseOrdersSummary.totalAmountARS)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Compr. USD</span>
                  <span className="font-semibold text-amount tabular-nums">U$S {formatMoney(purchaseOrdersSummary.totalAmountUSD)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Facturas proveedor */}
        {canSeeCompras && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Facturas proveedores</h2>
                <p className="text-xs text-muted-foreground">{supplierInvoicesSummary.totalInvoices} registradas</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <StackedBar segments={[
                { value: supplierInvoicesSummary.pendingCount,       color: 'bg-warning',     label: 'Pendiente' },
                { value: supplierInvoicesSummary.partiallyPaidCount, color: 'bg-info',        label: 'Parcial' },
                { value: supplierInvoicesSummary.paidCount,          color: 'bg-success',     label: 'Pagada' },
              ]} />
              <div className="space-y-1.5">
                {[
                  { label: 'Pendiente',           count: supplierInvoicesSummary.pendingCount,       dot: 'bg-warning' },
                  { label: 'Parcialmente pagada', count: supplierInvoicesSummary.partiallyPaidCount, dot: 'bg-info' },
                  { label: 'Pagada',              count: supplierInvoicesSummary.paidCount,          dot: 'bg-success' },
                ].map(({ label, count, dot }) => (
                  <div key={label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />
                      <span className="text-xs text-muted-foreground">{label}</span>
                    </div>
                    <span className="text-xs font-semibold tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-border space-y-1">
                <p className="text-xs font-medium text-muted-foreground mb-1">Deuda pendiente</p>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">ARS</span>
                  <span className="font-semibold text-destructive tabular-nums">$ {formatMoney(supplierInvoicesSummary.totalPendingAmountARS)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">USD</span>
                  <span className="font-semibold text-destructive tabular-nums">U$S {formatMoney(supplierInvoicesSummary.totalPendingAmountUSD)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ventas acumuladas */}
        {canSeeVentas && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <BarChart3 className="w-4 h-4 text-success" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Ventas — acumulado</h2>
                <p className="text-xs text-muted-foreground">{salesSummary.totalSales} ventas en total</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/40 rounded-xl p-4 text-center">
                  <p className="text-xs font-medium text-muted-foreground mb-1">ARS</p>
                  <p className="text-lg font-bold text-amount tabular-nums break-all">$ {formatMoney(salesSummary.totalSalesAmountARS)}</p>
                </div>
                <div className="bg-muted/40 rounded-xl p-4 text-center">
                  <p className="text-xs font-medium text-muted-foreground mb-1">USD</p>
                  <p className="text-lg font-bold text-amount tabular-nums break-all">U$S {formatMoney(salesSummary.totalSalesAmountUSD)}</p>
                </div>
              </div>
              <div className="divide-y divide-border">
                <div className="flex justify-between items-center py-2.5">
                  <span className="text-xs text-muted-foreground">Ventas este mes</span>
                  <span className="text-xs font-semibold text-info tabular-nums">{salesSummary.salesThisMonth}</span>
                </div>
                <div className="flex justify-between items-center py-2.5">
                  <span className="text-xs text-muted-foreground">Proveedores activos</span>
                  <span className="text-xs font-semibold tabular-nums">{entityCounts.activeSuppliers} / {entityCounts.totalSuppliers}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Últimas transacciones ── */}
      {canSeeFinanzas && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Layers className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Últimas transacciones</h2>
              <p className="text-xs text-muted-foreground">Los {recentTransactions.length} movimientos más recientes</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            {recentTransactions.length === 0 ? (
              <div className="py-12 text-center">
                <Layers className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-medium text-foreground">Sin transacciones recientes</h3>
                <p className="text-sm text-muted-foreground">Los movimientos aparecerán aquí</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">Descripción</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Monto</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase hidden md:table-cell">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransactions.map((tx: RecentTransaction, idx) => {
                    const statusCfg = TX_STATUS_CONFIG[tx.status] ?? { label: tx.status, className: 'bg-muted text-muted-foreground' };
                    const isLast = idx === recentTransactions.length - 1;
                    return (
                      <tr key={tx.id} className={cn('hover:bg-muted/30 transition-colors', !isLast && 'border-b border-border')}>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{tx.id}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="p-1.5 rounded-lg bg-muted shrink-0">{getTxIcon(tx.type)}</div>
                            <span className="text-sm font-medium text-foreground whitespace-nowrap">
                              {TX_TYPE_LABELS[tx.type] ?? tx.type}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell max-w-[200px]">
                          <span className="text-sm text-muted-foreground truncate block">{tx.description}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('inline-flex text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap', statusCfg.className)}>
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-amount tabular-nums">
                            {tx.currency === 'USD' ? 'U$S' : '$'} {formatMoney(tx.amount)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right hidden md:table-cell">
                          <div className="flex flex-col items-end">
                            <span className="text-sm text-foreground">
                              {new Date(tx.executedAt).toLocaleDateString('es-AR')}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(tx.executedAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {recentTransactions.length > 0 && (
            <div className="px-5 py-3.5 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Mostrando los últimos {recentTransactions.length} movimientos
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
